"use client"
import Link from "next/link";
import Image from "next/image";
import { SbBlokData } from "@storyblok/react/rsc";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import MenuItemWithDropdown from "./MenuItemWithDropdown";
import { cn } from "@/lib/utils";

interface StoryblokLink {
    id?: string;
    url?: string;
    linktype: "story" | "url" | "email" | "asset";
    fieldtype: "multilink";
    cached_url?: string;
    anchor?: string;
    target?: string;
}

interface StoryblokAsset {
    filename?: string;
    alt?: string;
}

interface NavItem {
    Label?: string;
    Link?: StoryblokLink;
    SubItem?: NavItem[];
}

export type NavVariant = "default" | "landingpage";

export type CtaItem = SbBlokData & {
    Type?: string;
    Label?: string;
    SubLabel?: string;
    NeuesFenster?: boolean;
    Link?: StoryblokLink;
};

interface FloatingNavProps {
    logo?: StoryblokAsset;
    navItems?: NavItem[];
    CTA?: CtaItem[];
    variant?: NavVariant;
}

function getLinkUrl(link?: StoryblokLink): string {
    if (!link) return "/";

    switch (link.linktype) {
        case "story":
            {
                const slug = link.cached_url || "";

                const cleaned =
                    slug === "pages" || slug === "landingpages"
                        ? ""
                        : slug.startsWith("pages/")
                            ? slug.slice("pages/".length)
                            : slug.startsWith("landingpages/")
                                ? slug.slice("landingpages/".length)
                                : slug;

                return `/${cleaned}`.replace(/\/+/g, "/");
            }
        case "url":
            return link.url || "/";
        case "email":
            return `mailto:${link.url || ""}`;
        case "asset":
            return link.url || "/";
        default:
            return "/";
    }
}

export default function FloatingNav({ logo, navItems, CTA, variant = "default" }: FloatingNavProps) {
    const isLandingpage = variant === "landingpage";
    const mainCta = CTA && CTA.length ? CTA[0] : null;
    const mainCtaWithType = mainCta ? { ...mainCta, Type: mainCta.Type || 'secondary' } : null;
    const items = isLandingpage ? [] : (navItems || []).filter((item) => item && item.Label && item.Link);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileDropdowns, setOpenMobileDropdowns] = useState<Record<string, boolean>>({});

    const pathname = usePathname();


    const navRef = useRef<HTMLDivElement | null>(null);
    const indicatorRef = useRef<HTMLDivElement | null>(null);
    const activeRef = useRef<HTMLAnchorElement | null>(null);

    const moveIndicator = useCallback((el: HTMLElement | null) => {
        if (!navRef.current || !indicatorRef.current || !el) return;
        const navRect = navRef.current.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        indicatorRef.current.style.left = `${elRect.left - navRect.left}px`;
        indicatorRef.current.style.top = `${elRect.top - navRect.top}px`;
        indicatorRef.current.style.width = `${elRect.width}px`;
        indicatorRef.current.style.height = `${elRect.height}px`;
        indicatorRef.current.style.opacity = "1";
    }, []);

    const hideIndicator = useCallback(() => {
        if (!indicatorRef.current) return;
        indicatorRef.current.style.opacity = "0";
    }, []);

    const updateActiveIndicator = useCallback(() => {
        if (!navRef.current || !indicatorRef.current) return;
        const active = navRef.current.querySelector<HTMLAnchorElement>('.nav-link.active-link');
        activeRef.current = active;
        if (!active) {
            hideIndicator();
            return;
        }
        moveIndicator(active);
    }, [hideIndicator, moveIndicator]);

    useEffect(() => {
        const nav = navRef.current;
        const indicator = indicatorRef.current;
        if (!nav || !indicator) return;

        const links = nav.querySelectorAll<HTMLElement>('.nav-link');
        const listeners: Array<{ el: HTMLElement; enter: () => void; leave: () => void }> = [];

        // Initial position without animation jump
        indicator.style.transition = 'none';
        updateActiveIndicator();
        requestAnimationFrame(() => {
            if (indicatorRef.current) indicatorRef.current.style.transition = '';
        });

        const handleResize = () => updateActiveIndicator();
        window.addEventListener('resize', handleResize);

        links.forEach((link) => {
            const onEnter = () => moveIndicator(link);
            const onLeave = () => {
                if (!activeRef.current) {
                    hideIndicator();
                    return;
                }
                moveIndicator(activeRef.current);
            };

            link.addEventListener('mouseenter', onEnter);
            link.addEventListener('mouseleave', onLeave);

            listeners.push({ el: link, enter: onEnter, leave: onLeave });
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            listeners.forEach(({ el, enter, leave }) => {
                el.removeEventListener('mouseenter', enter);
                el.removeEventListener('mouseleave', leave);
            });
        };
    }, [hideIndicator, moveIndicator, updateActiveIndicator]);

    useEffect(() => {
        updateActiveIndicator();
    }, [pathname, updateActiveIndicator]);

    useEffect(() => {
        if (!isMobileMenuOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMobileMenuOpen(false);
            }
        };

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isMobileMenuOpen]);

    const toggleMobileDropdown = (key: string) => {
        setOpenMobileDropdowns((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };


    return (
        <div className="fixed inset-x-0 top-4 z-50 flex justify-center">
            <div
                className={`${(isLandingpage && !mainCtaWithType) ? 'justify-center w-fit px-10' : 'lg:grid lg:grid-cols-4 justify-between w-full'} mx-4 flex container items-center gap-4 rounded-full px-4 py-3 shadow-lg shadow-slate-900/5 backdrop-blur-lg navigation-wrapper`}>
                <div className="flex items-center gap-3">
                    {logo?.filename ? (
                        <Link href="/" className="flex items-center gap-2" aria-label="Home">
                            <Image
                                src={logo.filename}
                                alt={logo.alt || "Logo"}
                                width={140}
                                height={40}
                                className="lg:h-8 h-6 w-auto object-contain"
                                priority
                            />
                        </Link>
                    ) : (
                        <Link href="/" className="text-base font-semibold">
                            basecloud
                        </Link>
                    )}
                </div>

                {!isLandingpage && (
                    <nav
                        ref={navRef}
                        className="nav hidden! flex-1 col-span-2 items-center justify-center gap-6 text-sm font-medium text-slate-200 lg:flex!">
                        <div ref={indicatorRef} className="nav-indicator"></div>
                        {items.map((item, index) => (
                            <MenuItemWithDropdown
                                key={`${item.Label}-${index}`}
                                item={item}
                                index={index}
                                pathname={pathname}
                                getLinkUrl={getLinkUrl}
                            />
                        ))}
                    </nav>
                )}

                {mainCtaWithType && (
                    <div className="hidden items-center justify-end gap-3 lg:flex text-white col-start-4">
                        {mainCtaWithType.Link && mainCtaWithType.Label && (
                            <Link
                                href={getLinkUrl(mainCtaWithType.Link)}
                                target={mainCtaWithType.NeuesFenster ? "_blank" : undefined}
                                rel={mainCtaWithType.NeuesFenster ? "noopener noreferrer" : undefined}
                                className={`main-button main-button--${mainCtaWithType.Type === 'secondary' || mainCtaWithType.Type === 'ghost' ? mainCtaWithType.Type : 'main'}`}
                            >
                                <span className="main-button-label">{mainCtaWithType.Label}</span>
                                {mainCtaWithType.SubLabel && mainCtaWithType.Type !== 'secondary' && (
                                    <span className="main-button-sublabel">{mainCtaWithType.SubLabel}</span>
                                )}
                            </Link>
                        )}
                    </div>
                )}

                {!isLandingpage && (
                    <div className="flex items-center justify-end lg:hidden">
                        <button
                            type="button"
                            aria-label="Menue oeffnen"
                            aria-expanded={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/20 text-white backdrop-blur transition hover:bg-black/30"
                        >
                            <span className="sr-only">Menue oeffnen</span>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            {!isLandingpage && isMobileMenuOpen && (
                <div
                    className="fixed inset-0 z-70 bg-black/55 backdrop-blur-sm lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation"
                >
                    <aside
                        className="absolute right-0 top-0 h-full w-[86vw] max-w-sm border-l border-white/15 bg-neutral-950/95 p-6 text-white shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="mb-8 flex items-center justify-between">
                            {logo?.filename ? (
                                <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2" aria-label="Home">
                                    <Image
                                        src={logo.filename}
                                        alt={logo.alt || "Logo"}
                                        width={120}
                                        height={34}
                                        className="h-8 w-auto"
                                    />
                                </Link>
                            ) : (
                                <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-semibold">
                                    basecloud
                                </Link>
                            )}

                            <button
                                type="button"
                                aria-label="Menue schliessen"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition hover:bg-white/10"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        <nav className="flex flex-col gap-1">
                            {items.map((item, index) => {
                                const href = getLinkUrl(item.Link);
                                const target = item.Link?.target;
                                const isActive = pathname === href;
                                const subItems = (item.SubItem || []).filter((subItem) => subItem && subItem.Label && subItem.Link);
                                const hasSubItems = subItems.length > 0;
                                const mobileKey = `${item.Label}-${index}`;
                                const isOpen = !!openMobileDropdowns[mobileKey];

                                if (!hasSubItems) {
                                    return (
                                        <Link
                                            key={`${item.Label}-mobile-${index}`}
                                            href={href}
                                            target={target}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className={`rounded-xl px-4 py-3 text-base transition ${isActive ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'}`}
                                        >
                                            {item.Label}
                                        </Link>
                                    );
                                }

                                return (
                                    <div key={`${item.Label}-mobile-${index}`} className="rounded-xl bg-white/5">
                                        <button
                                            type="button"
                                            onClick={() => toggleMobileDropdown(mobileKey)}
                                            className="flex w-full items-center justify-between px-4 py-3 text-left text-base text-slate-100 transition hover:bg-white/10"
                                            aria-expanded={isOpen}
                                            aria-controls={`mobile-submenu-${index}`}
                                        >
                                            <span>{item.Label}</span>
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                aria-hidden="true"
                                                className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                            >
                                                <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>

                                        {isOpen && (
                                            <div id={`mobile-submenu-${index}`} className="flex flex-col gap-1 px-2 pb-2">
                                                {subItems.map((subItem, subIndex) => {
                                                    const subHref = getLinkUrl(subItem.Link);
                                                    const subIsActive = pathname === subHref;

                                                    return (
                                                        <Link
                                                            key={`${subItem.Label}-mobile-sub-${index}-${subIndex}`}
                                                            href={subHref}
                                                            target={subItem.Link?.target}
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                            className={`rounded-lg px-3 py-2 text-sm transition ${subIsActive ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'}`}
                                                        >
                                                            <span className="block font-medium">{subItem.Label}</span>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </nav>

                        {mainCtaWithType?.Link && mainCtaWithType.Label && (
                            <div className="mt-8 border-t border-white/10 pt-6">
                                <Link
                                    href={getLinkUrl(mainCtaWithType.Link)}
                                    target={mainCtaWithType.NeuesFenster ? "_blank" : undefined}
                                    rel={mainCtaWithType.NeuesFenster ? "noopener noreferrer" : undefined}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`main-button main-button--${mainCtaWithType.Type === 'secondary' || mainCtaWithType.Type === 'ghost' ? mainCtaWithType.Type : 'main'}`}
                                >
                                    <span className="main-button-label">{mainCtaWithType.Label}</span>
                                    {mainCtaWithType.SubLabel && mainCtaWithType.Type !== 'secondary' && (
                                        <span className="main-button-sublabel">{mainCtaWithType.SubLabel}</span>
                                    )}
                                </Link>
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </div>
    );
}
